import { useModalLifecycle } from '../shared/useModalLifecycle'
/**
 * Team Spain v0 — Modal de Registro de Sesión Realizada (sRPE) (M6)
 * 
 * Permite al deportista registrar una sesión individual de entrenamiento realizada:
 * - Duración en minutos (>0) y esfuerzo percibido RPE (0 a 10).
 * - Carga sRPE = duración * RPE calculada en tiempo real.
 * - Tipo de actividad (Flag, Tackle, Fuerza, Competición, Otro) con campos condicionales.
 * - Origen (Club, Selección, Propio).
 * - Distingue sesión realizada de planificada.
 */

import React, { useState } from 'react'
import { X, Dumbbell, AlertCircle } from 'lucide-react'
import { formsService } from './formsService'
import { ActivityType, ActivityOrigin, TrainingLogItem } from '../../types/health'

interface TrainingLogModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (log: TrainingLogItem) => void
  teamId: string
  athleteId: string
  initialLog?: TrainingLogItem | null
}

const RPE_LABELS: Record<number, string> = {
  0: 'Reposo absoluto / Sin esfuerzo',
  1: 'Muy muy suave',
  2: 'Muy suave (recuperación activa)',
  3: 'Suave (ritmo cómodo)',
  4: 'Moderado (respiración acelerada)',
  5: 'Algo duro (esfuerzo mantenible)',
  6: 'Duro (cuesta hablar)',
  7: 'Muy duro (esfuerzo intenso)',
  8: 'Muy muy duro',
  9: 'Casi máximo (esfuerzo extenuante)',
  10: 'Máximo absoluto / Agotamiento total',
}

export const TrainingLogModal: React.FC<TrainingLogModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  teamId,
  athleteId,
  initialLog,
}) => {
  const [sessionDate, setSessionDate] = useState<string>(
    initialLog?.session_date || new Date().toISOString().split('T')[0]
  )
  const [sessionTime, setSessionTime] = useState<string>(initialLog?.session_time || '')
  const [activityType, setActivityType] = useState<ActivityType>(initialLog?.activity_type || 'flag')
  const [otherDescription, setOtherDescription] = useState<string>(
    initialLog?.other_activity_description || ''
  )
  const [durationMinutes, setDurationMinutes] = useState<number>(initialLog?.duration_minutes || 60)
  const [rpe, setRpe] = useState<number>(initialLog?.rpe !== undefined ? initialLog.rpe : 6)
  const [origin, setOrigin] = useState<ActivityOrigin>(initialLog?.origin || 'own')
  const [sportsComment, setSportsComment] = useState<string>(initialLog?.sports_comment || '')
  const [competitionMatches, setCompetitionMatches] = useState<number>(
    initialLog?.competition_matches_count || 1
  )
  const [competitionTournament, setCompetitionTournament] = useState<string>(
    initialLog?.competition_tournament || ''
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const isDirty = Boolean(
    sportsComment.trim() ||
    durationMinutes !== (initialLog?.duration_minutes || 60) ||
    rpe !== (initialLog?.rpe !== undefined ? initialLog.rpe : 6)
  )

  const handleCloseAttempt = React.useCallback(() => {
    if (isDirty) {
      if (!window.confirm('Tienes datos de sesión sin guardar. ¿Deseas descartarlos y cerrar?')) {
        return
      }
    }
    onClose()
  }, [isDirty, onClose])

  useModalLifecycle(isOpen, handleCloseAttempt)

  if (!isOpen) return null

  const calculatedLoad = Math.round(durationMinutes * rpe)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)

    if (durationMinutes <= 0) {
      setErrorMsg('La duración de la sesión debe ser superior a 0 minutos.')
      return
    }
    if (activityType === 'other' && !otherDescription.trim()) {
      setErrorMsg('Describe la actividad realizada cuando seleccionas "Otro".')
      return
    }

    setIsSubmitting(true)
    try {
      const saved = await formsService.saveTrainingLog({
        id: initialLog?.id,
        team_id: teamId,
        athlete_id: athleteId,
        session_date: sessionDate,
        session_time: sessionTime || null,
        activity_type: activityType,
        other_activity_description: activityType === 'other' ? otherDescription : null,
        duration_minutes: durationMinutes,
        rpe,
        origin,
        sports_comment: sportsComment.trim() || null,
        competition_matches_count: activityType === 'competition' ? competitionMatches : undefined,
        competition_tournament: activityType === 'competition' ? competitionTournament : undefined,
        confirmed_participation: true,
      })

      onSuccess(saved)
      onClose()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al guardar el entrenamiento.'
      setErrorMsg(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="mobile-viewport-overlay health-form-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCloseAttempt()
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(10, 19, 32, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
        padding: '1rem',
      }}
    >
      <div
        className="mobile-viewport-sheet"
        style={{
          backgroundColor: 'var(--surface-card)',
          borderRadius: '12px',
          padding: '1.5rem',
          maxWidth: '520px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-card)',
          border: '1px solid var(--surface-border)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: 'var(--brand-red-subtle)',
                color: 'var(--brand-red)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Dumbbell size={18} />
            </div>
            <div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
                {initialLog ? 'Editar sesión realizada' : 'Registrar sesión realizada'}
              </h2>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Cálculo de carga deportiva individual sRPE
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCloseAttempt}
            style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>

        {errorMsg && (
          <div
            style={{
              padding: '0.75rem',
              backgroundColor: 'var(--status-danger-bg)',
              color: 'var(--status-danger-text)',
              borderRadius: '8px',
              fontSize: '0.82rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1rem',
            }}
          >
            <AlertCircle size={16} />
            <span>{errorMsg}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Fecha y Hora */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Fecha *
              </label>
              <input
                type="date"
                required
                value={sessionDate}
                onChange={(e) => setSessionDate(e.target.value)}
                className="input"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Hora (opcional)
              </label>
              <input
                type="time"
                value={sessionTime}
                onChange={(e) => setSessionTime(e.target.value)}
                className="input"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          {/* Tipo de Actividad y Origen */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Tipo de actividad *
              </label>
              <select
                value={activityType}
                onChange={(e) => setActivityType(e.target.value as ActivityType)}
                className="input"
                style={{ width: '100%' }}
              >
                <option value="flag">Flag Football 5v5</option>
                <option value="strength">Fuerza / Gimnasio</option>
                <option value="tackle">Tackle Football</option>
                <option value="competition">Competición / Torneo</option>
                <option value="other">Otro deporte / actividad</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Ámbito / Origen *
              </label>
              <select
                value={origin}
                onChange={(e) => setOrigin(e.target.value as ActivityOrigin)}
                className="input"
                style={{ width: '100%' }}
              >
                <option value="club">Club propio</option>
                <option value="selection">Selección Nacional</option>
                <option value="own">Entrenamiento individual / propio</option>
              </select>
            </div>
          </div>

          {/* Campo condicional si es Otro */}
          {activityType === 'other' && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Descripción de la actividad *
              </label>
              <input
                type="text"
                required
                placeholder="Ej. Pádel, carrera continua, natación..."
                value={otherDescription}
                onChange={(e) => setOtherDescription(e.target.value)}
                className="input"
                style={{ width: '100%' }}
              />
            </div>
          )}

          {/* Campos condicionales si es Competición */}
          {activityType === 'competition' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)', gap: '0.75rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                  Partidos jugados
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={competitionMatches}
                  onChange={(e) => setCompetitionMatches(Number(e.target.value))}
                  className="input"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                  Torneo / Jornada
                </label>
                <input
                  type="text"
                  placeholder="Ej. Spanish Bowl / Liga Nacional"
                  value={competitionTournament}
                  onChange={(e) => setCompetitionTournament(e.target.value)}
                  className="input"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          )}

          {/* Duración en minutos */}
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Duración efectiva (minutos) *</label>
              <span style={{ fontSize: '0.85rem', fontWeight: 700, fontFamily: 'monospace' }}>
                {durationMinutes} min
              </span>
            </div>
            <input
              type="range"
              min="15"
              max="240"
              step="5"
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(Number(e.target.value))}
              style={{ width: '100%', accentColor: 'var(--brand-red)' }}
            />
            <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem' }}>
              {[30, 45, 60, 75, 90, 120].map((mins) => (
                <button
                  key={mins}
                  type="button"
                  onClick={() => setDurationMinutes(mins)}
                  className={`btn ${durationMinutes === mins ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '0.7rem', padding: '0.2rem 0.45rem', flex: 1 }}
                >
                  {mins}m
                </button>
              ))}
            </div>
          </div>

          {/* RPE (Esfuerzo Percibido 0 - 10) */}
          <div style={{ marginBottom: '1.25rem', backgroundColor: 'var(--surface-sunken)', padding: '0.85rem', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 700 }}>
                Esfuerzo percibido RPE (0–10) *
              </label>
              <span
                style={{
                  fontSize: '1.1rem',
                  fontWeight: 800,
                  fontFamily: 'monospace',
                  color: rpe >= 8 ? 'var(--brand-red)' : rpe >= 5 ? 'var(--brand-yellow)' : 'var(--status-success-text)',
                }}
              >
                {rpe} / 10
              </span>
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
              {RPE_LABELS[Math.round(rpe)] || 'Nivel de exigencia'}
            </div>
            <input
              type="range"
              min="0"
              max="10"
              step="0.5"
              value={rpe}
              onChange={(e) => setRpe(Number(e.target.value))}
              style={{ width: '100%', accentColor: 'var(--brand-red)' }}
            />

            {/* Badge de Carga sRPE Resultante */}
            <div
              style={{
                marginTop: '0.75rem',
                padding: '0.5rem 0.75rem',
                backgroundColor: 'var(--surface-card)',
                borderRadius: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid var(--surface-border)',
              }}
            >
              <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                Carga deportiva resultante (sRPE):
              </span>
              <span style={{ fontWeight: 800, fontFamily: 'monospace', fontSize: '0.95rem', color: 'var(--text-main)' }}>
                {calculatedLoad} UA
              </span>
            </div>
          </div>

          {/* Comentario deportivo */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
              Comentario o sensaciones (opcional)
            </label>
            <textarea
              rows={2}
              placeholder="Sensaciones, trabajo específico, ejercicios destacados..."
              value={sportsComment}
              onChange={(e) => setSportsComment(e.target.value)}
              className="input"
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>

          {/* Botones de acción */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : initialLog ? 'Actualizar sesión' : 'Guardar sesión realizada'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
